import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { getMcpTokenStatus, regenerateMcpToken, revokeMcpToken } from './mcp-tokens-client';
import type { McpTokenRegenerateResponse, McpTokenStatus } from './mcp-tokens-client.type';

const MCP_TOKEN_QUERY_KEY = ['mcp-token-status'] as const;

/**
 * Query hook returning the authenticated user's MCP token status.
 *
 * @returns {ReturnType<typeof useQuery<McpTokenStatus>>} React Query result
 *   exposing `data`, `isLoading`, `isError`, etc.
 */
function useMcpTokenStatus(): UseQueryResult<McpTokenStatus> {
  return useQuery<McpTokenStatus>({
    queryKey: MCP_TOKEN_QUERY_KEY,
    queryFn: getMcpTokenStatus,
    staleTime: 1000 * 30,
  });
}

/**
 * Mutation hook that regenerates the user's MCP token. On success the
 * status query is invalidated so any displayed metadata refreshes.
 *
 * @returns {ReturnType<typeof useMutation<McpTokenRegenerateResponse>>} React
 *   Query mutation whose `mutateAsync()` returns the raw token payload.
 */
function useRegenerateMcpToken(): UseMutationResult<McpTokenRegenerateResponse, unknown, void, unknown> {
  const queryClient = useQueryClient();

  return useMutation<McpTokenRegenerateResponse, unknown, void>({
    mutationFn: () => regenerateMcpToken(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MCP_TOKEN_QUERY_KEY });
    },
  });
}

/**
 * Mutation hook that revokes the user's MCP token. On success the status
 * query is invalidated so the UI flips back to the "no token" state.
 *
 * @returns {ReturnType<typeof useMutation<void>>} React Query mutation.
 */
function useRevokeMcpToken(): UseMutationResult<void, unknown, void, unknown> {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, void>({
    mutationFn: () => revokeMcpToken(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MCP_TOKEN_QUERY_KEY });
    },
  });
}

export { useMcpTokenStatus, useRegenerateMcpToken, useRevokeMcpToken };
